import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import type { MouseEvent} from 'react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
// import { Check, Close, Edit, LabelHintIcon } from '../icons'

import type { ICarOffer } from 'BrokerTsLib/src/dtos'

import { usePriceFormat } from '@/hooks/usePriceFormat'

import { EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons'

export type EditableTotalProps = {
  label?: string
  size?: string
  fontWeight?: string
  color?: string
  price: ICarOffer['price']
  priceMin?: number
  priceMax?: number
  isEditable: boolean
  tooltip?: string
  onChange: (price: number, successAction: () => void, errorAction: (error: string) => void) => void
  onIsEditingChange?: (isEditing: boolean) => void
  renderPrice?: (price: string | number | null | undefined, handleEdit: () => void) => React.ReactNode
}



export const EditableTotal = ({
  label = '',
  size = 'md',
  fontWeight = 'semibold',
  color = 'gray.100',
  price,
  priceMin,
  priceMax,
  isEditable,
  tooltip,
  onChange,
  onIsEditingChange,
  renderPrice
}: EditableTotalProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { formatPrice, priceFormat, currencyUnit } = usePriceFormat()
  const [defaultPrice, setDefaultPrice] = useState<number | undefined>(price)
  const [isEditingPrice, setEditingPrice] = useState<boolean>(false)

  const [errorMessage, setErrorMessage] = useState<string>('')

  // Reset default price when new price prop is passed
  useMemo(() => {
    setDefaultPrice(price)
  }, [price])

  // Focus input and select all after start of editing
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }

    return () => { }
  }, [inputRef, isEditingPrice])

  const handleSaveSuccess = () => {
    setErrorMessage('')
    setEditingPrice(false)
  }

  const handleSaveError = (error: string) => {
    setErrorMessage(error)
  }

  // Set new price
  const setPrice = (value: string) => {
    let newValue: number = parseInt(value)

    if (isNaN(newValue)) {
      newValue = 0
    }

    if (newValue > 1000000000) {
      newValue = 1000000000
    }

    if (newValue != price) {
      onChange(newValue, handleSaveSuccess, handleSaveError)
    } else {
      handleSaveSuccess()
    }
  }

  // Handle cancel event
  const handleCancel = () => {
    if (typeof defaultPrice !== 'undefined') {
      setPrice(String(defaultPrice))
    }
  }

  // Handle saving events
  const handlePriceSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const value = e?.currentTarget?.price?.value

    setPrice(value)
  }

  const handlePriceButtonSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const value = e?.currentTarget?.form?.price?.value

    setPrice(value)
  }

  useEffect(() => {
    onIsEditingChange?.(isEditingPrice)
  }, [isEditingPrice])

  // Start editing price
  const handleEdit = () => {
    if (!isEditable) return
    setEditingPrice(true)
  }

  // Do not show anything when price is undefined
  if (typeof price === 'undefined') {
    return null
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, alignItems: 'start' }}>
      <Box
        sx={{
          display: 'flex',
          alignContent: 'start',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: size == 'sm' ? 0 : 2,
          bgColor: color,
          borderRadius: 'base',
          lineHeight: '1.25rem',
        }}
      >
        {!isEditingPrice && (
          <>
            <Text sx={{ fontSize: 'base', mr: 2 }}>{label}</Text>
            <Flex sx={{ gap: 2, alignItems: 'center' }}>

              {isEditable && (
                <Button variant="ghost" size="xl" onClick={handleEdit} title="Upravit cenu" px={0}>
                  {/* <Edit color="secondary.500" /> */}
                  <EditIcon />
                  {/* Upravit */}
                </Button>
              )}

              {renderPrice && renderPrice(price, handleEdit)}
              {!renderPrice &&
                <Text
                  sx={{ py: size == 'sm' ? 0 : 2, fontSize: size === 'lg' ? 'xl' : size, fontWeight }}
                  title="Upravit cenu"
                  onClick={handleEdit}
                >
                  {formatPrice({ price })}
                </Text>
              }

            </Flex>
          </>
        )}

        {isEditingPrice && (
          <Stack direction="column" rowGap={2}>
            <Text sx={{ fontSize: 'base' }}>{label}</Text>

            <form onSubmit={handlePriceSubmit}>
              <Flex sx={{ gap: 4, justifyContent: 'end' }}>
                <InputGroup size="md">
                  <Input
                    name="price"
                    ref={inputRef}
                    type="number"
                    // htmlSize={8}
                    variant='filled'
                    size='md'
                    inputMode="numeric"
                    step={1}
                    defaultValue={Math.round(price)}
                  />

                  <InputRightElement>{currencyUnit}</InputRightElement>
                </InputGroup>

                <Flex sx={{ gap: 1, alignItems: 'center' }}>
                  <Button
                    // variant="ghost"
                    size="xs"
                    onClick={handleCancel}
                    title="Zrušit úpravy"
                    p={2}
                  >

                    <CloseIcon />
                  </Button>

                  <Button
                    // variant="solid"
                    size="xs"
                    onClick={handlePriceButtonSubmit}
                    title="Uložit"
                    p={2}
                  >
                    <CheckIcon />
                  </Button>
                </Flex>
              </Flex>
            </form>

            {errorMessage && <Text variant={'danger'}>{errorMessage}</Text>}

            {priceMin && priceMax && (
              <Box>
                <Text fontSize='sm'>
                  Zadejte hodnotu od <Text as="strong">{priceFormat(priceMin)}</Text> do{' '}
                  <Text as="strong">{priceFormat(priceMax)}</Text>
                  {tooltip && (
                    <Tooltip hasArrow label={tooltip} placement="top">
                      <Text as="span" sx={{ lineHeight: 0 }}>
                        {/* &nbsp;<LabelHintIcon width={4} height={4} /> */}
                      </Text>
                    </Tooltip>
                  )}
                </Text>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
